import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import './advance-select.scss';
import { CloseIcon, InfoIcon, ShortDownIcon } from '../icons/icons';
import Skeleton from '../skeleton/skeleton';

const AdvanceSelectLabel = forwardRef(
  (
    {
      placeholder,
      onChange,
      data,
      handleInputChanged,
      defaultValue,
      label,
      errorMessage,
      message,
      required,
      disabled,
      className,
      maxLength,
      removeSelectedValue,
      subCompany,
      subLabel,
      arrowColor,
      scrollHandler,
      disableSearch,
      setSearchLocationValueQuery, // This is query separately defined to store user input for BE search from add locations my-company
      searchLocationValueQuery, // This is query separately defined to store user input for BE search from add locations my-company
      backendSearchFlag, // This is flag to identify this is location tab  defined for BE search from add locations my-company
      islocationListLoading, // This is loaderflag  defined for BE search from add locations my-company
      searchMaterialValueQuery,
      setSearchMaterialValueQuery,
      isMaterialLoading,
      searchUserValueQuery,
      setSearchUserValueQuery,
      isUserListLoading,
      name,
      setActiveDropDown,
      triggerClearField, // This prop is used to clear the field value
      onInputChange,
      inputClassName,
      onKeyUp,
      customOptions,
      renderOption,
      labelClass,
      textboxClass,
      infoIcon,
      msgClassName,
    },
    ref
  ) => {
    const [t] = useTranslation('company');
    const flag = useRef(false);
    const [query, setQuery] = useState('');
    const [selectedValue, setSelectedValue] = useState(defaultValue?.value);
    const [selectedLabel, setSelectedLabel] = useState(defaultValue?.label);
    const [openDDL, setOpenDDl] = useState(false);
    const [getTop, setGetTop] = useState();
    const [getLeft, setGetLeft] = useState();
    const [getWidth, setGetWidth] = useState();
    const listHeight = useRef();
    const [searchValue, setSearchValue] = useState('');
    const boxHeight = useRef(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const optionRefs = useRef([]);
    const inputRef = useRef(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      clear: () => {
        if (inputRef.current) inputRef.current.value = '';
      },
      get value() {
        return inputRef.current?.value;
      },
    }));

    useEffect(() => {
      if (subCompany) {
        flag.current = true;
        const headquarter = data?.filter(
          (item) => item.label === 'Headquarter'
        );
        if (headquarter?.length > 0) {
          setSelectedLabel(headquarter[0]?.label);
          setSelectedValue(headquarter[0]?.value);
        }
      } else if (flag.current === true && subCompany === false) {
        flag.current = false;
        setSelectedLabel('');
      }
    }, [subCompany]);

    useEffect(() => {
      if (triggerClearField) {
        setSelectedValue(null);
        setSelectedLabel('');
        setSearchValue('');
        setSearchLocationValueQuery('');
        setSearchMaterialValueQuery('');
        setSearchUserValueQuery('');
        setQuery('');
        document.body.style.overflow = 'auto';
      }
    }, [triggerClearField]);

    useEffect(() => {
      setSearchValue('');
      setSearchLocationValueQuery('');
      setSearchMaterialValueQuery('');
      setSearchUserValueQuery('');
      setOpenDDl(false);
      setSelectedLabel(defaultValue?.label);
    }, [defaultValue?.label]);

    useEffect(() => {
      if (!defaultValue?.value && query && !openDDL) {
        setOpenDDl(true);
      }
    }, [query]);

    const getValue = (e) => {
      setSelectedValue(e.target.value);
      if (customOptions) {
        setSelectedLabel(e.target.nextSibling.innerHTML);
      } else {
        setSelectedLabel(e.target.nextSibling.innerText);
      }

      onChange(e);
      setOpenDDl(false);
      setSearchValue('');
      setSearchLocationValueQuery('');
      setSearchMaterialValueQuery('');
      setSearchUserValueQuery('');
      document.body.style.overflow = 'auto';
    };
    const getDLPosition = () => {
      setTimeout(() => {
        if (
          boxHeight.current.getBoundingClientRect().top +
            boxHeight.current.getBoundingClientRect().height +
            listHeight.current.getBoundingClientRect().height >
          window.innerHeight
        ) {
          setGetTop(
            boxHeight.current.getBoundingClientRect().top -
              listHeight.current.getBoundingClientRect().height
          );
        } else {
          setGetTop(
            boxHeight.current.getBoundingClientRect().top +
              boxHeight.current.getBoundingClientRect().height
          );
        }
      }, 200);
    };
    const getPosition = () => {
      setActiveDropDown(name);
      setOpenDDl(true);
      getDLPosition();
      setQuery('');
      // setSearchValue('');
      document.body.style.overflow = 'hidden';
      setGetWidth(boxHeight?.current?.getBoundingClientRect()?.width);
      setGetLeft(boxHeight?.current?.getBoundingClientRect()?.left);
    };

    const handleChange = (e) => {
      removeSelectedValue();
      const rawValue = e?.target?.value || '';
      const value = rawValue.replace(/[^a-zA-Z0-9@&.\s]/g, '');
      if (value === '') {
        setSearchValue('');
        setSelectedLabel('');
        removeSelectedValue();
        setSelectedValue(null);
      }
      onInputChange({
        ...e,
        target: {
          ...e.target,
          value,
        },
      });
      setOpenDDl(true);
      setQuery(value.toLowerCase());
      handleInputChanged(value);
      setSelectedValue(null);
      setSelectedLabel('');
      getDLPosition();
      setSearchValue(value);
      setSearchLocationValueQuery(value);
      setSearchMaterialValueQuery(value);
      setSearchUserValueQuery(value);
    };

    const closeDropdown = () => {
      setOpenDDl(false);
      document.body.style.overflow = 'auto';
      if (selectedLabel) {
        setSelectedLabel(selectedLabel);
      } else {
        setSelectedLabel(defaultValue?.label || '');
      }
    };
    const onClear = () => {
      setSearchValue('');
      setSelectedLabel('');
      removeSelectedValue();
      setSelectedValue(null);
      setOpenDDl(true);
      getDLPosition();
      inputRef.current?.focus();
      setQuery('');
    };
    const removeValue = (e) => {
      onKeyUp(e);
      if (e.key === 'Backspace') {
        if (e?.target?.value?.length < 1) {
          setSearchValue('');
          setSelectedLabel('');
          removeSelectedValue();
          setSelectedValue(null);
          setSelectedLabel(null);
        }
      }
    };

    const isDisabled = (disable) => {
      if (disable) return 'disabled';
      return '';
    };

    useEffect(() => {
      if (
        focusedIndex < 0 ||
        !optionRefs.current[focusedIndex] ||
        !listHeight.current
      ) {
        return;
      }

      const optionEl = optionRefs.current[focusedIndex];
      const listEl = listHeight.current;

      const optionTop = optionEl.offsetTop;
      const optionBottom = optionTop + optionEl.offsetHeight;

      const listScrollTop = listEl.scrollTop;
      const listVisibleHeight = listEl.clientHeight;

      // scroll down
      if (optionBottom > listScrollTop + listVisibleHeight) {
        listEl.scrollTop = optionBottom - listVisibleHeight;
      }

      // scroll up
      if (optionTop < listScrollTop) {
        listEl.scrollTop = optionTop;
      }
    }, [focusedIndex]);

    useEffect(() => {
      if (!openDDL || !listHeight.current) return;

      const selectedIndex = data.findIndex(
        (item) => String(item.value) === String(selectedValue)
      );

      if (selectedIndex < 0) return;

      const optionEl = optionRefs.current[selectedIndex];
      const listEl = listHeight.current;

      if (!optionEl) return;

      const optionTop = optionEl.offsetTop;
      const optionBottom = optionTop + optionEl.offsetHeight;

      const listScrollTop = listEl.scrollTop;
      const listVisibleHeight = listEl.clientHeight;

      if (optionBottom > listScrollTop + listVisibleHeight) {
        listEl.scrollTop = optionBottom - listVisibleHeight;
      }

      if (optionTop < listScrollTop) {
        listEl.scrollTop = optionTop;
      }

      // IMPORTANT: sync keyboard focus
      setFocusedIndex(selectedIndex);
    }, [openDDL]);

    return (
      <div className={`advance-select${openDDL ? ' active' : ''} ${className}`}>
        {label && (
          <label
            htmlFor={
              searchLocationValueQuery ||
              searchMaterialValueQuery ||
              searchUserValueQuery ||
              searchValue
            }
            className={`${labelClass}`}
          >
            {/* searchLocationValueQuery and other Query are defined to store value for BE search separately in add location my-company and material list */}
            {label} {required && <span className="required">*</span>}
          </label>
        )}
        <div
          ref={boxHeight}
          onClick={getPosition}
          onKeyUp={removeValue}
          role="button"
          tabIndex="0"
          className={`textbox-wrap${disabled ? ' disabled' : ''}`}
        >
          <div className={`textbox-area ${textboxClass}`} ref={boxHeight}>
            {customOptions ? (
              <>
                {selectedLabel && (
                  <div className="value-area w-100-20">
                    <span className="ddl-option-label">
                      {ReactHtmlParser(selectedLabel)}
                    </span>
                  </div>
                )}
                {!selectedLabel && (
                  <input
                    type="text"
                    placeholder={placeholder}
                    onChange={handleChange}
                    ref={inputRef}
                    value={searchValue}
                    disabled={disabled}
                    id={
                      searchLocationValueQuery ||
                      searchMaterialValueQuery ||
                      searchUserValueQuery ||
                      searchValue
                    }
                    maxLength={maxLength}
                    onClick={getPosition}
                    onKeyUp={removeValue}
                    className={`position-static ${inputClassName}`}
                    readOnly={disableSearch}
                    onKeyDown={(e) => {
                      if (!openDDL) return;

                      const visibleOptions =
                        (searchLocationValueQuery ||
                        searchMaterialValueQuery ||
                        searchUserValueQuery
                          ? data
                          : data?.filter((val) =>
                              val.label
                                ?.toLowerCase()
                                .includes(query?.toLowerCase())
                            )) || [];

                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setFocusedIndex((prev) =>
                          prev < visibleOptions.length - 1 ? prev + 1 : 0
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setFocusedIndex((prev) =>
                          prev > 0 ? prev - 1 : visibleOptions.length - 1
                        );
                      } else if (e.key === 'Enter' && focusedIndex >= 0) {
                        e.preventDefault();
                        const selectedOption = visibleOptions[focusedIndex];
                        if (selectedOption && !selectedOption.disabled) {
                          const fakeEvent = {
                            target: {
                              value: selectedOption.value,
                              nextSibling: { innerText: selectedOption.label },
                            },
                          };
                          getValue(fakeEvent);
                        }
                      }
                    }}
                  />
                )}
              </>
            ) : (
              <input
                className={inputClassName}
                type="text"
                placeholder={placeholder}
                onChange={handleChange}
                ref={inputRef}
                value={
                  selectedLabel !== ''
                    ? selectedLabel
                    : searchLocationValueQuery ||
                      searchMaterialValueQuery ||
                      searchUserValueQuery ||
                      searchValue
                }
                disabled={disabled}
                id={
                  searchLocationValueQuery ||
                  searchMaterialValueQuery ||
                  searchUserValueQuery ||
                  searchValue
                }
                maxLength={maxLength}
                onClick={getPosition}
                onKeyUp={removeValue}
                readOnly={disableSearch}
                onKeyDown={(e) => {
                  if (!openDDL) return;

                  const visibleOptions =
                    (searchLocationValueQuery ||
                    searchMaterialValueQuery ||
                    searchUserValueQuery
                      ? data
                      : data?.filter((val) =>
                          val.label
                            ?.toLowerCase()
                            .includes(query?.toLowerCase())
                        )) || [];

                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setFocusedIndex((prev) =>
                      prev < visibleOptions.length - 1 ? prev + 1 : 0
                    );
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setFocusedIndex((prev) =>
                      prev > 0 ? prev - 1 : visibleOptions.length - 1
                    );
                  } else if (e.key === 'Enter' && focusedIndex >= 0) {
                    e.preventDefault();
                    const selectedOption = visibleOptions[focusedIndex];
                    if (selectedOption && !selectedOption.disabled) {
                      const fakeEvent = {
                        target: {
                          value: selectedOption.value,
                          nextSibling: { innerText: selectedOption.label },
                        },
                      };
                      getValue(fakeEvent);
                    }
                  }
                }}
              />
            )}

            {subLabel !== '' && <span className="sub-label">{subLabel}</span>}

            {disableSearch || !selectedLabel || disabled ? (
              <span className="arrow">
                <ShortDownIcon color={arrowColor} />
              </span>
            ) : (
              <button
                type="button"
                className="clear bg-transparent border-none p-0"
                onClick={onClear}
              >
                <CloseIcon size="0.972vw" />
              </button>
            )}
          </div>
        </div>

        {openDDL && (
          <div
            className="select-mask"
            onClick={closeDropdown}
            onKeyDown={closeDropdown}
            role="button"
            tabIndex="0"
          >
            &nbsp;
          </div>
        )}
        {customOptions ? (
          openDDL && (
            <ul
              className="advance-select-options show"
              style={{ width: getWidth, top: getTop, left: getLeft }}
              ref={listHeight}
              onScroll={(e) => {
                scrollHandler(e);
              }}
            >
              {
                // BE search is active
                (((searchLocationValueQuery ||
                  searchMaterialValueQuery ||
                  searchUserValueQuery) &&
                  !islocationListLoading &&
                  !isMaterialLoading &&
                  !isUserListLoading &&
                  data?.length === 0) ||
                  // FE search only
                  (!searchLocationValueQuery &&
                    !searchMaterialValueQuery &&
                    !searchUserValueQuery &&
                    !islocationListLoading &&
                    !isMaterialLoading &&
                    !isUserListLoading &&
                    query &&
                    data?.filter((val) =>
                      val.label?.toLowerCase().includes(query.toLowerCase())
                    )?.length === 0)) && (
                  <li>
                    <label className="option">
                      <span className="label no-data">
                        {t('companydetailscreen.abouttab.noresultplaceholder')}
                      </span>
                    </label>
                  </li>
                )
              }

              {/* This loader only for BE search */}

              {backendSearchFlag &&
                (islocationListLoading ||
                  isMaterialLoading ||
                  isUserListLoading) &&
                Array.from({
                  length: 25,
                }).map(() => (
                  <li>
                    <Skeleton
                      skHeight="2.361vw"
                      skWidth="100%"
                      skRadius="0.100vw"
                      skMargin="0 0 0.556vw 0"
                    />
                  </li>
                ))}
              {/* searchLocationValueQuery and other Query are for BE search if its not present we can use FE search for others */}
              {(searchLocationValueQuery ||
              searchMaterialValueQuery ||
              searchUserValueQuery
                ? data
                : data?.filter((val) =>
                    val.label?.toLowerCase().includes(query?.toLowerCase())
                  )
              )?.map((val, index) => (
                <li key={val.value}>
                  <label
                    className={`option ${
                      selectedValue ===
                      (typeof selectedValue === 'string'
                        ? String(val.value)
                        : Number(val.value))
                        ? 'active'
                        : isDisabled(val.disabled)
                    }${focusedIndex === index ? ' focused' : ''}`}
                    htmlFor={val.value}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                  >
                    <input
                      type="radio"
                      onChange={getValue}
                      onClick={getValue}
                      name="ddOptions"
                      value={name === 'materialGroup' ? val.label : val.value}
                      disabled={val.disabled}
                      defaultChecked={selectedValue === val.value}
                      id={val.value}
                    />
                    <span className="label">{renderOption(val)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )
        ) : (
          <ul
            className={`advance-select-options ${openDDL ? 'show' : ''}`}
            style={{ width: getWidth, top: getTop, left: getLeft }}
            ref={listHeight}
            onScroll={(e) => {
              scrollHandler(e);
            }}
          >
            {
              // BE search is active
              (((searchLocationValueQuery ||
                searchMaterialValueQuery ||
                searchUserValueQuery) &&
                !islocationListLoading &&
                !isMaterialLoading &&
                !isUserListLoading &&
                data?.length === 0) ||
                // FE search only
                (!searchLocationValueQuery &&
                  !searchMaterialValueQuery &&
                  !searchUserValueQuery &&
                  !islocationListLoading &&
                  !isMaterialLoading &&
                  !isUserListLoading &&
                  query &&
                  data?.filter((val) =>
                    val.label?.toLowerCase().includes(query.toLowerCase())
                  )?.length === 0)) && (
                <li>
                  <label className="option">
                    <span className="label no-data">
                      {t('companydetailscreen.abouttab.noresultplaceholder')}
                    </span>
                  </label>
                </li>
              )
            }

            {/* This loader only for BE search */}

            {backendSearchFlag &&
              (islocationListLoading ||
                isMaterialLoading ||
                isUserListLoading) &&
              Array.from({
                length: 25,
              }).map(() => (
                <li>
                  <Skeleton
                    skHeight="2.361vw"
                    skWidth="100%"
                    skRadius="0.100vw"
                    skMargin="0 0 0.556vw 0"
                  />
                </li>
              ))}
            {/* searchLocationValueQuery and other Query are for BE search if its not present we can use FE search for others */}
            {(searchLocationValueQuery ||
            searchMaterialValueQuery ||
            searchUserValueQuery
              ? data
              : data?.filter((val) =>
                  val.label?.toLowerCase().includes(query?.toLowerCase())
                )
            )?.map((val, index) => (
              <li key={val.value}>
                <label
                  className={`option ${
                    selectedValue ===
                    (typeof selectedValue === 'string'
                      ? String(val.value)
                      : Number(val.value))
                      ? 'active'
                      : isDisabled(val.disabled)
                  }${focusedIndex === index ? ' focused' : ''}`}
                  htmlFor={val.value}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                >
                  <input
                    type="radio"
                    onChange={getValue}
                    onClick={getValue}
                    name="ddOptions"
                    value={name === 'materialGroup' ? val.label : val.value}
                    disabled={val.disabled}
                    defaultChecked={selectedValue === val.value}
                    id={val.value}
                  />
                  <span className="label">{val.label}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {message !== '' && (
          <p className={`message ${msgClassName}`}>
            {infoIcon && (
              <span className="mr-5 d-flex">
                <InfoIcon width="0.694vw" height="0.694vw" color="#616B80" />
              </span>
            )}{' '}
            {message}
          </p>
        )}
        {errorMessage !== '' && <p className="error-message">{errorMessage}</p>}
      </div>
    );
  }
);
export default AdvanceSelectLabel;

AdvanceSelectLabel.propTypes = {
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  data: PropTypes.instanceOf(Array),
  handleInputChanged: PropTypes.func,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  message: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  // forceValue: PropTypes.string,
  maxLength: PropTypes.number,
  // isReadOnly: PropTypes.bool,
  removeSelectedValue: PropTypes.func,
  subCompany: PropTypes.bool,
  subLabel: PropTypes.string,
  arrowColor: PropTypes.string,
  scrollHandler: PropTypes.func,
  setSearchLocationValueQuery: PropTypes.func,
  searchLocationValueQuery: PropTypes.string,
  backendSearchFlag: PropTypes.bool,
  islocationListLoading: PropTypes.bool,
  searchMaterialValueQuery: PropTypes.string,
  setSearchMaterialValueQuery: PropTypes.func,
  isMaterialLoading: PropTypes.bool,
  name: PropTypes.string,
  setActiveDropDown: PropTypes.func,
  searchUserValueQuery: PropTypes.string,
  setSearchUserValueQuery: PropTypes.func,
  isUserListLoading: PropTypes.bool,
  triggerClearField: PropTypes.bool,
  onInputChange: PropTypes.func,
  disableSearch: PropTypes.bool,
  inputClassName: PropTypes.string,
  onKeyUp: PropTypes.func,
  customOptions: PropTypes.bool,
  renderOption: PropTypes.func,
  labelClass: PropTypes.string,
  textboxClass: PropTypes.string,
  infoIcon: PropTypes.bool,
  msgClassName: PropTypes.string,
};
AdvanceSelectLabel.defaultProps = {
  required: false,
  placeholder: '',
  defaultValue: '',
  onChange: () => {},
  data: [],
  handleInputChanged: () => {},
  label: '',
  errorMessage: '',
  message: '',
  disabled: false,
  className: '',
  // forceValue: '',
  maxLength: 50,
  // isReadOnly: false,
  removeSelectedValue: () => {},
  subCompany: false,
  subLabel: '',
  arrowColor: '',
  scrollHandler: () => {},
  setSearchLocationValueQuery: () => {},
  searchLocationValueQuery: '',
  backendSearchFlag: false,
  islocationListLoading: false,
  searchMaterialValueQuery: '',
  setSearchMaterialValueQuery: () => {},
  isMaterialLoading: false,
  name: '',
  setActiveDropDown: () => {},
  searchUserValueQuery: '',
  setSearchUserValueQuery: () => {},
  isUserListLoading: false,
  triggerClearField: false,
  onInputChange: () => {},
  disableSearch: false,
  inputClassName: '',
  onKeyUp: () => {},
  customOptions: false,
  renderOption: () => {},
  labelClass: '',
  textboxClass: '',
  infoIcon: false,
  msgClassName: '',
};
